import { serviceWindow } from '@aeris/communication';
/**
 * A janela de 24h vem do domínio, não de uma cópia aqui.
 *
 * A aritmética é de três linhas e a tentação de reescrevê-la é proporcional —
 * mas é exatamente por isso que `@aeris/communication` é um pacote puro: o
 * servidor recusa o envio pela mesma função que a tela usa para desabilitar o
 * campo. Duas cópias divergem no dia em que uma delas mudar, e o sintoma
 * seria a tela oferecer um envio que o servidor recusa (ou pior, o contrário).
 * É o mesmo motivo que faz `@aeris/money` decidir o troco nos dois lados.
 *
 * O que fica aqui é só a tradução: a API entrega instantes como string ISO, e
 * o domínio fala em `Date`.
 */
export const windowStateFor = (lastInboundAt, at) => serviceWindow({
    lastInboundAt: lastInboundAt === null ? null : new Date(lastInboundAt),
    at,
});
/** "3h20", ou "45min" quando falta menos de uma hora. */
export const formatRemaining = (from, to) => {
    const totalMinutes = Math.max(0, Math.round((to.getTime() - from.getTime()) / 60_000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0)
        return `${String(minutes)}min`;
    return `${String(hours)}h${String(minutes).padStart(2, '0')}`;
};
/**
 * Como esta conversa se chama na tela — F084.
 *
 * A ordem é uma decisão de produto, não uma coincidência de `??`:
 *
 * 1. o nome do **cadastro** vinculado, porque é como a LOJA chama a pessoa —
 *    e é o nome que aparece na venda, no orçamento e no financeiro;
 * 2. o nome de **perfil** do WhatsApp, que é como a pessoa chama a si mesma;
 * 3. o número, quando não há nenhum dos dois.
 *
 * Um cadastro ARQUIVADO não chega aqui: o servidor devolve `contactPartyName`
 * nulo para ele, de propósito.
 */
export const contactLabelOf = (conversation, 
/**
 * O nome que ESTE atendente deu ao contato (01/10), que vence os três: é
 * só ele quem vê. Ver `use-contact-names.ts`.
 */
ownName = null) => ownName ??
    // Num grupo (F268) o nome do GRUPO vem antes do da loja: "Imperio Cell +
    // Elite" diz que é o grupo, e a loja aparece no marcador.
    (conversation.contactKind === 'group'
        ? (conversation.contactProfileName ?? conversation.contactPartyName)
        : (conversation.contactPartyName ?? conversation.contactProfileName)) ??
    contactPhoneOf(conversation);
/** Se o rótulo acima é um NOME ou só o número repetido. */
export const contactHasName = (conversation, ownName = null) => ownName !== null ||
    conversation.contactPartyName !== null ||
    conversation.contactProfileName !== null;
/**
 * De onde ele fala — F086.
 *
 * A cidade e a UF do cadastro vinculado, juntas como se escreve no Brasil:
 * `Ponta Grossa/PR`. A UF entra porque "Ponta Grossa" sozinha é ambígua (há
 * uma em Pernambuco também) e custa duas letras; e entra do lado da cidade,
 * não como uma segundo marcador, porque são uma informação só.
 *
 * `null` — e portanto marcador NENHUM — quando o contato não tem cadastro,
 * quando o cadastro foi arquivado, ou quando ele não tem endereço. Uma
 * marcador escrito "sem cidade" ocuparia o mesmo espaço para não informar
 * nada, e o cabeçalho de uma conversa é o lugar mais caro da tela.
 */
export const contactCityOf = (conversation) => {
    const city = conversation.contactPartyCity;
    if (city === null)
        return null;
    const uf = conversation.contactPartyUf;
    return uf === null ? city : `${city}/${uf}`;
};
export const inboxTagsOf = (conversation, party, 
/**
 * O nome do número por onde esta conversa chega (F091), ou `null` para não
 * mostrar marcador de canal nenhuma.
 *
 * Quem chama passa `null` em dois casos, e os dois são o mesmo raciocínio:
 * a loja tem UM número (não há o que distinguir), ou a pessoa já escolheu um
 * número no seletor (todas as conversas na tela são dele, e repetir o nome
 * em cada uma é ruído que come as quatro vagas da fileira).
 */
channelLabel) => {
    /*
     * O marcador do canal vem PRIMEIRO, e vem em todos os caminhos de saída
     * abaixo — inclusive no "Sem cadastro", que é a maioria de um inbox real.
     *
     * Primeiro porque é o contexto que muda a leitura de todas as outras: saber
     * que a conversa chegou pelo número do suporte, e não pelo da loja, decide
     * como se lê "Em atraso" e o que se responde. As demais qualificam o
     * cliente; esta qualifica a conversa inteira.
     */
    const channelTag = channelLabel === null
        ? null
        : {
            id: 'channel',
            kind: 'channel',
            label: channelLabel,
            srLabel: `Recebida pelo WhatsApp ${channelLabel}`,
            tone: 'neutral',
            action: null,
        };
    const withChannel = (tags) => channelTag === null ? tags : [channelTag, ...tags];
    /*
     * Sem cadastro, esta é a única marcador possível SOBRE O CLIENTE — e é a
     * única que importa. Cidade, crédito e compras são todos do cadastro; sem
     * ele não existe nenhum deles, e o que se pode fazer a respeito é uma coisa
     * só: dizer quem é este número. (O canal não é sobre o cliente, e por isso
     * sobrevive aqui.)
     */
    if (conversation.contactPartyId === null) {
        if (conversation.contactKind === 'group') {
            return withChannel([
                {
                    id: 'no-party',
                    kind: 'no-party',
                    label: 'Grupo sem loja',
                    srLabel: 'Grupo sem loja ligada. O gerente liga o grupo à loja em Configurações do atendimento, Grupos.',
                    tone: 'warning',
                    action: null,
                },
            ]);
        }
        return withChannel([
            {
                id: 'no-party',
                kind: 'no-party',
                label: 'Sem cadastro',
                srLabel: 'Sem cadastro. Abrir o painel para identificar este número.',
                tone: 'warning',
                action: 'panel',
            },
        ]);
    }
    const city = contactCityOf(conversation);
    /*
     * A cidade é a única que também existe enquanto o detalhe carrega: ela vem
     * do resumo da conversa, que já está na mão.
     */
    const cityTag = city === null
        ? null
        : {
            id: 'city',
            kind: 'city',
            label: city,
            srLabel: `Cidade: ${city}`,
            tone: 'neutral',
            action: null,
        };
    // Ainda não se sabe o resto. Ver o comentário do tipo.
    if (party === undefined)
        return withChannel(cityTag === null ? [] : [cityTag]);
    /*
     * Vínculo gravado, cadastro fora do ar. O caso que o dono viveu na F084:
     * arquivou a pessoa em Pessoas e o inbox continuou anunciando o cliente.
     * O marcador leva ao painel porque é lá que mora "Desvincular".
     */
    if (party === null) {
        return withChannel([
            ...(cityTag === null ? [] : [cityTag]),
            {
                id: 'archived-party',
                kind: 'archived-party',
                label: 'Cadastro arquivado',
                srLabel: 'Cadastro arquivado. Abrir o painel para desvincular.',
                tone: 'warning',
                action: 'panel',
            },
        ]);
    }
    /*
     * `credit` é nulo quando não há crédito concedido E nada em aberto — não há
     * o que dizer, e portanto não há atraso.
     */
    const overdue = party.credit?.overdueCount ?? 0;
    const manual = [
        ...conversation.tags.map((tag) => ({
            id: tag.id,
            kind: 'conversation-tag',
            label: tag.name,
            srLabel: `Marcador desta conversa: ${tag.name}`,
            tone: tag.tone,
            action: null,
        })),
        ...party.tags.map((tag) => ({
            id: tag.id,
            kind: 'party-tag',
            label: tag.name,
            srLabel: `Marcador do cliente: ${tag.name}`,
            tone: tag.tone,
            action: null,
        })),
    ];
    /*
     * A ordem da fileira é "o quanto isto muda o que você vai fazer nos próximos
     * trinta segundos":
     *
     *   0. por qual dos meus números isto chegou (`withChannel`, F091);
     *   1. o que DECIDE uma venda — títulos vencidos;
     *   2. o assunto corrente — os marcadores desta conversa;
     *   3. quem é este cliente sempre — os marcadores do cadastro;
     *   4. o contexto — a cidade, ou o convite a preenchê-la;
     *   5. o que é bom saber — cliente novo.
     *
     * Importa porque a fileira CORTA quando não cabe: quem some é o fim dela, e
     * some com um contador dizendo quantas ficaram de fora.
     */
    return withChannel([
        ...(overdue > 0
            ? [
                {
                    id: 'overdue',
                    kind: 'overdue',
                    label: `${String(overdue)} em atraso`,
                    srLabel: `${String(overdue)} ${overdue === 1 ? 'título vencido' : 'títulos vencidos'} no contas a receber`,
                    tone: 'danger',
                    action: null,
                },
            ]
            : []),
        ...manual,
        cityTag ?? {
            id: 'add-city',
            kind: 'add-city',
            label: 'Cidade',
            srLabel: 'Adicionar a cidade ao cadastro',
            tone: 'neutral',
            action: 'party',
        },
        /*
         * Zero DOCUMENTOS, e não zero vendas: orçamento e pedido contam. Quem já
         * pediu um orçamento não é mais alguém que a loja nunca atendeu, e tratá-lo
         * como novo é o tipo de erro que o cliente percebe.
         */
        ...(party.purchases.totalDocuments === 0
            ? [
                {
                    id: 'new-customer',
                    kind: 'new-customer',
                    label: 'Cliente novo',
                    srLabel: 'Cliente novo: nenhuma compra, pedido ou orçamento registrado',
                    tone: 'info',
                    action: null,
                },
            ]
            : []),
    ]);
};
/**
 * O número formatado como uma pessoa lê — F085.
 *
 * `5542999959582` é um dado; `+55 42 99995-9582` é um telefone. A tela mostra
 * o segundo, e a busca continua aceitando os dois (o servidor compara pelos
 * dígitos — ver `digitsOf` no repositório).
 *
 * Só formata o que reconhece: um número fora do padrão brasileiro volta como
 * veio, porque inventar um agrupamento para um número estrangeiro é pior do
 * que mostrar os dígitos crus.
 */
export const formatWaId = (waId) => {
    const match = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(waId);
    if (match === null)
        return waId;
    return `+55 ${match[1] ?? ''} ${match[2] ?? ''}-${match[3] ?? ''}`;
};
/**
 * O telefone do contato como a tela o escreve — ou "Número oculto" (F192).
 *
 * No WhatsApp por aparelho vinculado a identidade é o LID, e o número só vem
 * quando o WhatsApp o revela; quem chega pelo nome de usuário pode nunca
 * mostrá-lo. Os dígitos do LID NÃO são telefone e nunca aparecem aqui:
 * formatados, pareceriam um número estrangeiro que ninguém atende.
 */
export const contactPhoneOf = (conversation) => 
// Um grupo (F268) não tem telefone: o lugar do número diz de quem é o grupo.
conversation.contactKind === 'group'
    ? conversation.contactPartyName === null ||
        conversation.contactPartyName === undefined
        ? 'Grupo do WhatsApp'
        : `Grupo · ${conversation.contactPartyName}`
    : conversation.contactWaId === null
        ? 'Número oculto'
        : formatWaId(conversation.contactWaId);
/**
 * A semente da cor do avatar: o que identifica a pessoa e não muda — o
 * telefone, e o LID quando o telefone não veio.
 */
export const contactSeedOf = (conversation) => conversation.contactWaId ?? conversation.contactLid ?? conversation.contactId;
//# sourceMappingURL=inbox.js.map